/**
 * Authentication modal that switches between login and register forms
 */
import React, { useState } from 'react';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { EnhancedRegisterForm } from './EnhancedRegisterForm';
import { FmModal, FmText } from '@/components/ui';

interface AuthModalProps {
  open: boolean;
  onCancel: () => void;
  defaultMode?: 'login' | 'register';
  useEnhancedRegistration?: boolean;
  onAuthenticated?: () => void;
  title?: string;
  context?: string;
  closeLabel?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({ 
  open, 
  onCancel, 
  defaultMode = 'login',
  useEnhancedRegistration = true,
  onAuthenticated,
  title,
  context,
  closeLabel,
}) => {
  const [mode, setMode] = useState<'login' | 'register'>(defaultMode);

  const handleSuccess = () => {
    if (onAuthenticated) {
      onAuthenticated();
      return;
    }
    onCancel();
  };

  const handleSwitchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
  };

  const handleRegistrationSuccess = () => {
    // Registration creates an UNVERIFIED account (email-verification gate), so we
    // must NOT navigate into the protected app here — the user has to verify their
    // email first. Switch to the login form; they can sign in after clicking the
    // verification link in their email.
    setMode('login');
  };

  // Reset mode when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setMode(defaultMode);
    }
  }, [open, defaultMode]);

  return (
    <FmModal
      open={open}
      onCancel={onCancel}
      title={title}
      cancelText={closeLabel}
      footer={null}
      size={mode === 'register' && useEnhancedRegistration ? 'lg' : 'md'}
    >
      {context ? <FmText size="body" tone="muted">{context}</FmText> : null}
      {mode === 'login' ? (
        <LoginForm 
          onSuccess={handleSuccess}
          onSwitchToRegister={handleSwitchMode}
        />
      ) : (
        useEnhancedRegistration ? (
          <EnhancedRegisterForm
            onSuccess={handleRegistrationSuccess}
            onCancel={handleSwitchMode}
          />
        ) : (
          <RegisterForm 
            onSuccess={handleSwitchMode} // Switch to login after successful registration
            onSwitchToLogin={handleSwitchMode}
          />
        )
      )}
    </FmModal>
  );
};
